import { useState, type FormEvent } from 'react';
import { PlatformApiError, type Viewer } from '@sfc/sdk';
import { platform } from '../api/client';
import { Icon } from '../ui/Icon';

/** The floor the API enforces. Repeated here so the refusal arrives before the request does. */
const MINIMUM_LENGTH = 12;

interface AccountPanelProps {
  viewer: Viewer;
}

/**
 * Your own account.
 *
 * Every role reaches this, including the ones with no staff capabilities at
 * all: somebody handed a one-time passphrase has to be able to replace it with
 * something they chose, and needing an administrator for that is how a
 * passphrase ends up in a chat message forever.
 */
export function AccountPanel({ viewer }: AccountPanelProps): React.JSX.Element {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [problem, setProblem] = useState<string | undefined>();
  const [done, setDone] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  // Checked here so the answer is immediate, and again by the API, which is the
  // one that counts.
  const tooShort = newPassword.length > 0 && newPassword.length < MINIMUM_LENGTH;
  const mismatched = confirmation.length > 0 && confirmation !== newPassword;
  const unchanged = newPassword.length > 0 && newPassword === currentPassword;
  const ready =
    currentPassword.length > 0 &&
    newPassword.length >= MINIMUM_LENGTH &&
    confirmation === newPassword &&
    !unchanged;

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setProblem(undefined);
    setDone(undefined);
    setBusy(true);

    try {
      const result = await platform.auth.changePassword({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmation('');
      setDone(
        result.otherSessionsEnded > 0
          ? `Password changed. ${result.otherSessionsEnded} other ${
              result.otherSessionsEnded === 1 ? 'session was' : 'sessions were'
            } signed out.`
          : 'Password changed. You were not signed in anywhere else.',
      );
    } catch (error) {
      setProblem(explain(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card" aria-labelledby="section-heading">
      <div className="panel">
        <div className="panel__head">
          <div>
            <h2>Signed in as</h2>
            <p className="panel__hint" data-testid="account-identity">
              {viewer.user.displayName} · {viewer.user.email} · {viewer.role} in {viewer.organization.name}
            </p>
          </div>
        </div>
      </div>

      <form className="panel" onSubmit={submit} aria-labelledby="change-password-heading">
        <div className="panel__head">
          <div>
            <h2 id="change-password-heading">Change your password</h2>
            <p className="panel__hint">
              Your current password is asked for as well, because being signed in says which browser is asking,
              not who is at it. Changing it signs out every other device; this one stays signed in.
            </p>
          </div>
        </div>

        <div className="form-grid">
          <div className="field field--wide">
            <label htmlFor="current-password">Current password</label>
            <input
              id="current-password"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </div>

          <div className="field field--half">
            <label htmlFor="new-password">New password</label>
            <input
              id="new-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={MINIMUM_LENGTH}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              aria-describedby="new-password-hint"
            />
            <p className="panel__hint" id="new-password-hint">
              At least {MINIMUM_LENGTH} characters. A phrase you can remember beats a short one you cannot.
            </p>
          </div>

          <div className="field field--half">
            <label htmlFor="confirm-password">New password again</label>
            <input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              required
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
          </div>
        </div>

        {/* One line at a time, and only once the field it is about has been
            typed in: a form that complains about every empty box on arrival is
            read as noise and then not read at all. */}
        {tooShort ? (
          <p className="notice notice--problem" data-testid="password-hint">
            That is {MINIMUM_LENGTH - newPassword.length} character
            {MINIMUM_LENGTH - newPassword.length === 1 ? '' : 's'} short.
          </p>
        ) : unchanged ? (
          <p className="notice notice--problem" data-testid="password-hint">
            That is the password you already have.
          </p>
        ) : mismatched ? (
          <p className="notice notice--problem" data-testid="password-hint">
            The two new passwords do not match.
          </p>
        ) : null}

        {problem ? (
          <p className="notice notice--problem" role="alert" data-testid="password-problem">
            {problem}
          </p>
        ) : null}

        {done ? (
          <p className="notice notice--once" role="status" data-testid="password-changed">
            <Icon name="lock" size={18} />
            <span>{done}</span>
          </p>
        ) : null}

        <div className="button-row">
          <button className="button button--primary" type="submit" disabled={!ready || busy}>
            {busy ? 'Changing…' : 'Change password'}
          </button>
        </div>
      </form>
    </section>
  );
}

function explain(error: unknown): string {
  if (error instanceof PlatformApiError) {
    // 401 here is the current password being wrong, not a session that expired:
    // an expired session would have been caught before this screen rendered.
    if (error.status === 401) return 'That is not your current password.';
    if (error.status === 400) return error.message;
  }
  return 'The password was not changed. Something went wrong.';
}
